import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

export interface ExplorerSteerConfig {
  inject_enabled: boolean
  stop_redirect_enabled: boolean
}

interface EditExplorerSteerDialogProps {
  isOpen: boolean
  config: ExplorerSteerConfig
  onSave: (config: ExplorerSteerConfig) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function EditExplorerSteerDialog({
  isOpen,
  config,
  onSave,
  onOpenChange,
}: EditExplorerSteerDialogProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [injectEnabled, setInjectEnabled] = useState(true)
  const [stopRedirectEnabled, setStopRedirectEnabled] = useState(true)

  useEffect(() => {
    setInjectEnabled(config.inject_enabled)
    setStopRedirectEnabled(config.stop_redirect_enabled)
  }, [config])

  const handleSubmit = async () => {
    setSaving(true)
    try {
      await onSave({
        inject_enabled: injectEnabled,
        stop_redirect_enabled: stopRedirectEnabled,
      })
      toast.success(
        t("adminDashboard.features.explorerSteer.success", {
          defaultValue: "Explorer steering configuration updated",
        })
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.explorerSteer.error", {
          defaultValue: "Failed to update explorer steering configuration",
        }) + (error instanceof Error ? `: ${error.message}` : "")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.features.explorerSteer.configTitle", {
              defaultValue: "Explorer Steering",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.features.explorerSteer.configDescription", {
              defaultValue:
                "Configure whether users can send guidance into a running Explorer turn and stop a turn with partial-answer persistence.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="explorer-steer-inject">
                {t("adminDashboard.features.explorerSteer.injectLabel", {
                  defaultValue: "Mid-run steering (inject)",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerSteer.injectHelp", {
                  defaultValue:
                    "Let users send guidance while Explorer is still generating a response; it is injected before the next step.",
                })}
              </p>
            </div>
            <Switch
              checked={injectEnabled}
              id="explorer-steer-inject"
              onCheckedChange={setInjectEnabled}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="explorer-steer-stop-redirect">
                {t("adminDashboard.features.explorerSteer.stopRedirectLabel", {
                  defaultValue: "Stop & redirect with partial persistence",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.features.explorerSteer.stopRedirectHelp", {
                  defaultValue:
                    "Let users stop a running turn and persist the partial answer instead of discarding it, then optionally redirect with new instructions.",
                })}
              </p>
            </div>
            <Switch
              checked={stopRedirectEnabled}
              id="explorer-steer-stop-redirect"
              onCheckedChange={setStopRedirectEnabled}
            />
          </div>
        </div>

        <DialogFooter>
          <Button disabled={saving} variant="outline" onClick={() => onOpenChange(false)}>
            {t("adminDashboard.features.explorerSteer.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={saving} onClick={handleSubmit}>
            {saving
              ? t("adminDashboard.features.explorerSteer.saving", {
                  defaultValue: "Saving...",
                })
              : t("adminDashboard.features.explorerSteer.save", {
                  defaultValue: "Save",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
